import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Search, Package2, Plus, Box } from 'lucide-react';
import { getMaterials, Material } from '../../lib/materials';
import { getStockItems, StockItem } from '../../lib/stockItems';
import { useBusiness } from '../../contexts/BusinessContext';
import { useModalZIndex } from '../../contexts/ModalStackContext';
import MaterialModal from '../MaterialModal';

type MaterialSource = 'materials' | 'inventory';

interface SelectMaterialModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (material: Material | StockItem, quantity: number, source: MaterialSource) => void;
  businessId?: string;
}

export default function SelectMaterialModal({
  isOpen,
  onClose,
  onAdd,
  businessId
}: SelectMaterialModalProps) {
  const { selectedBusiness } = useBusiness();
  const stackZ = useModalZIndex(100010);
  const [isAnimating, setIsAnimating] = useState(false);
  const [activeTab, setActiveTab] = useState<MaterialSource>('materials');
  const [searchQuery, setSearchQuery] = useState('');
  const [materials, setMaterials] = useState<Material[]>([]);
  const [stockItems, setStockItems] = useState<StockItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedMaterials, setSelectedMaterials] = useState<Set<string>>(new Set());
  const [quantities, setQuantities] = useState<Record<string, number>>({});
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const loadMaterials = async () => {
    if (!selectedBusiness?.id) return;

    try {
      setLoading(true);
      const data = await getMaterials(selectedBusiness.id);
      setMaterials(data);
      setError(null);
    } catch (err) {
      console.error('Error loading materials:', err);
      setError(err instanceof Error ? err.message : 'Failed to load materials');
    } finally {
      setLoading(false);
    }
  };

  const loadStockItems = async () => {
    if (!selectedBusiness?.id) return;

    try {
      setLoading(true);
      const data = await getStockItems(selectedBusiness.id);
      // Filter out items with 0 quantity (already handled by getStockItems, but double-check)
      const availableItems = data.filter(item => item.quantity > 0);
      setStockItems(availableItems);
      setError(null);
    } catch (err) {
      console.error('Error loading stock items:', err);
      setError(err instanceof Error ? err.message : 'Failed to load inventory items');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      if (activeTab === 'materials') {
        loadMaterials();
      } else {
        loadStockItems();
      }
    }
  }, [isOpen, selectedBusiness?.id, activeTab]);

  const handleTransitionEnd = () => {
    if (!isOpen) {
      setIsAnimating(false);
      setSearchQuery('');
      setSelectedMaterials(new Set());
      setQuantities({});
    }
  };

  const filteredMaterials = materials.filter(material => {
    const searchLower = searchQuery.toLowerCase();
    return (
      material.name.toLowerCase().includes(searchLower) ||
      material.description?.toLowerCase().includes(searchLower)
    );
  });

  const filteredStockItems = stockItems.filter(item => {
    const searchLower = searchQuery.toLowerCase();
    return (
      item.name.toLowerCase().includes(searchLower) ||
      item.description?.toLowerCase().includes(searchLower)
    );
  });

  const handleAddMaterial = (material: Material) => {
    const quantity = quantities[material.id] || 1;
    onAdd(material, quantity, 'materials');
    
    // Add to selected materials
    setSelectedMaterials(prev => {
      const next = new Set(prev);
      next.add(material.id);
      return next;
    });
    // Close modal after successful add
    onClose();
  };

  const handleAddStockItem = (item: StockItem) => {
    const quantity = quantities[item.id] || 1;
    // Validate quantity doesn't exceed available stock
    if (quantity > item.quantity) {
      setError(`Only ${item.quantity} ${item.unit}(s) available in stock`);
      return;
    }
    onAdd(item, quantity, 'inventory');
    
    // Add to selected items
    setSelectedMaterials(prev => {
      const next = new Set(prev);
      next.add(item.id);
      return next;
    });
    // Close modal after successful add
    onClose();
  };

  if (!isOpen && !isAnimating) return null;

  const isMobile = window.innerWidth < 640;

  const modalContent = (
    <div 
      className={`fixed inset-0 transition-opacity duration-200 ${
        isOpen ? 'bg-black/50' : 'bg-black/0'
      }`}
      style={{
        zIndex: stackZ,
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        paddingTop: isMobile ? 'env(safe-area-inset-top, 0px)' : '0',
        paddingBottom: isMobile ? 'env(safe-area-inset-bottom, 0px)' : '0'
      }}
      onClick={onClose}
      onTransitionEnd={handleTransitionEnd}
    >
      <div 
        className={`bg-white transform transition-all duration-200 ${
          isMobile 
            ? `relative w-full h-full ${isOpen ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'}`
            : `fixed inset-y-0 right-0 w-full md:w-[600px] shadow-2xl ${
                isOpen ? 'translate-x-0' : 'translate-x-full'
              }`
        }`}
        style={{ 
          fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
          zIndex: stackZ + 40
        }}
        onClick={e => e.stopPropagation()}
      >
        <div className="h-full flex flex-col overflow-hidden">
          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 h-8 w-8 flex items-center justify-center text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 active:bg-gray-200 active:scale-95 transition-all duration-150 touch-manipulation z-10"
            style={{ WebkitTapHighlightColor: 'transparent' }}
          >
            <X className="h-5 w-5" />
          </button>

          {/* Header */}
          <div className="bg-gray-50 border-b border-gray-200 px-6 py-6 relative">
            <h2 className="text-2xl font-bold text-gray-900 text-center mb-4" style={{ 
              letterSpacing: '-0.02em'
            }}>
              Select Material
            </h2>
            
            {/* Tab Toggle */}
            <div className="flex items-center justify-center space-x-2 bg-white rounded-xl p-1">
              <button
                onClick={() => setActiveTab('materials')}
                className={`flex-1 px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-150 ${
                  activeTab === 'materials'
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Materials
              </button>
              <button
                onClick={() => setActiveTab('inventory')}
                className={`flex-1 px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-150 ${
                  activeTab === 'inventory'
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Inventory
              </button>
            </div>
          </div>

          {/* Search Bar */}
          <div className="bg-white border-b border-gray-200 px-6 py-4">
            <div className="flex items-center space-x-3">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder={activeTab === 'materials' ? 'Search materials...' : 'Search inventory...'}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 bg-white border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                  style={{ fontSize: '16px' }}
                />
              </div>
              {activeTab === 'materials' && (
                <button
                  onClick={() => setIsCreateModalOpen(true)}
                  className="inline-flex items-center px-4 py-3 font-semibold text-white bg-blue-600 rounded-xl hover:bg-blue-700 active:bg-blue-800 active:scale-[0.98] transition-all duration-150 touch-manipulation"
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  <Plus className="h-4 w-4 mr-1.5" />
                  Add
                </button>
              )}
            </div>
          </div>

          {/* Content */}
          <div 
            className="flex-1 overflow-y-auto bg-white"
            style={{ 
              WebkitOverflowScrolling: 'touch',
              paddingBottom: isMobile ? 'env(safe-area-inset-bottom, 0px)' : '0'
            }}
          >
            <div className={`${isMobile ? 'p-4' : 'p-6'} space-y-4`}>
              {loading ? (
                <div className="flex items-center justify-center h-64">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-4 text-sm text-gray-600">Loading materials...</p>
                  </div>
                </div>
              ) : error ? (
                <div className="bg-white rounded-[12px] border border-gray-200/60 p-4 shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
                  <div className="text-center">
                    <p className="text-sm text-red-600 mb-3">{error}</p>
                    <button
                      onClick={loadMaterials}
                      className="px-4 py-2 text-sm font-semibold text-blue-600 bg-blue-50 hover:bg-blue-100 active:bg-blue-200 active:scale-[0.98] rounded-xl transition-all duration-150 touch-manipulation"
                      style={{ WebkitTapHighlightColor: 'transparent' }}
                    >
                      Try again
                    </button>
                  </div>
                </div>
              ) : activeTab === 'materials' ? (
                filteredMaterials.length === 0 ? (
                  <div className="bg-white rounded-[12px] border border-gray-200/60 p-8 shadow-[0_1px_2px_rgba(0,0,0,0.05)] text-center">
                    <Package2 className="h-10 w-10 text-gray-400 mx-auto mb-3" />
                    <p className="text-sm font-semibold text-gray-900 mb-1">
                      {searchQuery ? 'No matching materials' : 'No materials available'}
                    </p>
                    <p className="text-sm text-gray-500 mb-4">
                      {searchQuery ? 'Try adjusting your search terms' : 'Add materials to track costs and inventory'}
                    </p>
                    {!searchQuery && (
                      <button
                        onClick={() => setIsCreateModalOpen(true)}
                        className="inline-flex items-center px-4 py-3 font-semibold text-blue-600 bg-blue-50 hover:bg-blue-100 active:bg-blue-200 active:scale-[0.98] rounded-xl transition-all duration-150 touch-manipulation"
                        style={{ WebkitTapHighlightColor: 'transparent' }}
                      >
                        <Plus className="h-4 w-4 mr-1.5" />
                        Add Material
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="space-y-3">
                    {filteredMaterials.map((material) => {
                      const isSelected = selectedMaterials.has(material.id);
                      return (
                        <div 
                          key={material.id} 
                          className="bg-white rounded-[12px] border border-gray-200/60 p-4 shadow-[0_1px_2px_rgba(0,0,0,0.05)] active:scale-[0.98] transition-all duration-150"
                          style={{ touchAction: 'manipulation' }}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1 min-w-0">
                              <h3 className="text-sm font-semibold text-gray-900 truncate">{material.name}</h3>
                              {material.description && (
                                <p className="mt-1 text-sm text-gray-600 line-clamp-2">{material.description}</p>
                              )}
                              <div className="mt-1.5 text-xs text-gray-500">
                                ${material.cost.toFixed(2)} per {material.unit}
                              </div>
                              {!isSelected && (
                                <div className="mt-3 flex items-center space-x-2">
                                  <input
                                    type="number"
                                    min="0.01"
                                    step="0.01"
                                    value={quantities[material.id] || ''}
                                    placeholder="Qty"
                                    onChange={(e) => setQuantities(prev => ({
                                      ...prev,
                                      [material.id]: parseFloat(e.target.value) || 0
                                    }))}
                                    className="w-20 h-9 px-3 text-sm border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                                    style={{ fontSize: '16px' }}
                                  />
                                  <span className="text-sm text-gray-500">{material.unit}(s)</span>
                                </div>
                              )}
                            </div>
                            <button
                              onClick={() => handleAddMaterial(material)}
                              disabled={isSelected || !quantities[material.id] || quantities[material.id] <= 0 || (material.quantity !== undefined && quantities[material.id] > material.quantity)}
                              className={`inline-flex items-center px-4 py-2 text-xs font-semibold rounded-xl transition-all duration-150 active:scale-[0.98] touch-manipulation ml-3 flex-shrink-0 ${
                                isSelected
                                  ? 'bg-green-100 text-green-800 cursor-not-allowed'
                                  : !quantities[material.id] || quantities[material.id] <= 0 || (material.quantity !== undefined && quantities[material.id] > material.quantity)
                                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                  : 'text-white bg-blue-600 hover:bg-blue-700 active:bg-blue-800'
                              }`}
                              style={{ WebkitTapHighlightColor: 'transparent' }}
                            >
                              {isSelected ? 'Added' : 'Add'}
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )
              ) : (
                filteredStockItems.length === 0 ? (
                  <div className="bg-white rounded-[12px] border border-gray-200/60 p-8 shadow-[0_1px_2px_rgba(0,0,0,0.05)] text-center">
                    <Box className="h-10 w-10 text-gray-400 mx-auto mb-3" />
                    <p className="text-sm font-semibold text-gray-900 mb-1">
                      {searchQuery ? 'No matching inventory items' : 'No inventory items available'}
                    </p>
                    <p className="text-sm text-gray-500 mb-4">
                      {searchQuery ? 'Try adjusting your search terms' : 'Inventory items with available stock will appear here'}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {filteredStockItems.map((item) => {
                      const isSelected = selectedMaterials.has(item.id);
                      const availableQty = item.quantity;
                      const requestedQty = quantities[item.id] || 0;
                      const exceedsStock = requestedQty > availableQty;
                      
                      return (
                        <div 
                          key={item.id} 
                          className="bg-white rounded-[12px] border border-gray-200/60 p-4 shadow-[0_1px_2px_rgba(0,0,0,0.05)] active:scale-[0.98] transition-all duration-150"
                          style={{ touchAction: 'manipulation' }}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1 min-w-0">
                              <h3 className="text-sm font-semibold text-gray-900 truncate">{item.name}</h3>
                              {item.description && (
                                <p className="mt-1 text-sm text-gray-600 line-clamp-2">{item.description}</p>
                              )}
                              <div className="mt-1.5 flex items-center space-x-3">
                                <div className="text-xs text-gray-500">
                                  Cost: ${item.acquisition_cost.toFixed(2)} per {item.unit}
                                </div>
                                <div className="text-xs text-blue-600 font-medium">
                                  Stock: {availableQty} {item.unit}(s)
                                </div>
                              </div>
                              {!isSelected && (
                                <div className="mt-3 flex items-center space-x-2">
                                  <input
                                    type="number"
                                    min="0.01"
                                    step="0.01"
                                    max={availableQty}
                                    value={quantities[item.id] || ''}
                                    placeholder="Qty"
                                    onChange={(e) => setQuantities(prev => ({
                                      ...prev,
                                      [item.id]: parseFloat(e.target.value) || 0
                                    }))}
                                    className="w-20 h-9 px-3 text-sm border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                                    style={{ fontSize: '16px' }}
                                  />
                                  <span className="text-sm text-gray-500">{item.unit}(s)</span>
                                </div>
                              )}
                              {exceedsStock && requestedQty > 0 && (
                                <p className="mt-1 text-xs text-red-600">
                                  Only {availableQty} available
                                </p>
                              )}
                            </div>
                            <button
                              onClick={() => handleAddStockItem(item)}
                              disabled={isSelected || !quantities[item.id] || quantities[item.id] <= 0 || exceedsStock}
                              className={`inline-flex items-center px-4 py-2 text-xs font-semibold rounded-xl transition-all duration-150 active:scale-[0.98] touch-manipulation ml-3 flex-shrink-0 ${
                                isSelected
                                  ? 'bg-green-100 text-green-800 cursor-not-allowed'
                                  : !quantities[item.id] || quantities[item.id] <= 0 || exceedsStock
                                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                  : 'text-white bg-blue-600 hover:bg-blue-700 active:bg-blue-800'
                              }`}
                              style={{ WebkitTapHighlightColor: 'transparent' }}
                            >
                              {isSelected ? 'Added' : 'Add'}
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )
              )}
            </div>
          </div>
        </div>
      </div>

      <MaterialModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={() => {
          setIsCreateModalOpen(false);
          loadMaterials();
        }}
        businessId={selectedBusiness?.id}
        mode="create"
      />
    </div>
  );

  return createPortal(modalContent, document.body);
}